import { GuideButton, useGuide, useGuideValue } from "../guides/GuideProvider";
import axios from "axios";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const DEFAULT_VIDEO_PATH = "Ingen video vald";
const DRAFT_STORAGE_KEY = "createSessionDraft";

function createEmptyStationDraft() {
  return {
    title: "",
    description: "",
    videoPath: DEFAULT_VIDEO_PATH
  };
}

function createDraftStation(station) {
  return {
    draftId: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: station.title || "",
    description: station.description || "",
    videoPath: station.videoUrl || station.videoPath || DEFAULT_VIDEO_PATH
  };
}

function moveItem(list, fromIndex, toIndex) {
  const updatedList = [...list];
  const [movedItem] = updatedList.splice(fromIndex, 1);
  updatedList.splice(toIndex, 0, movedItem);
  return updatedList;
}

function CreateSession({ exercise, setExercise }) {
  const { report } = useGuide();
  const navigate = useNavigate();
  const location = useLocation();
  const initializedFromState = useRef(false);
  const hasHydratedDraft = useRef(false);
  const draggedStationId = useRef(null);

  const [session, setSession] = useState({
    title: "",
    description: ""
  });
  const [stationDraft, setStationDraft] = useState(createEmptyStationDraft);
  const [stations, setStations] = useState([]);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  useGuideValue("session-title", Boolean(session.title.trim()));
  useGuideValue("station-title", Boolean(stationDraft.title.trim()));
  useGuideValue("selected-video", stationDraft.videoPath !== DEFAULT_VIDEO_PATH);


  const draftPayload = useMemo(() => ({
    session,
    stationDraft,
    stations,
    selectedTemplateId
  }), [session, stationDraft, stations, selectedTemplateId]);

  function resetBuilder() {
    setSession({ title: "", description: "" });
    setStationDraft(createEmptyStationDraft());
    setStations([]);
    setSelectedTemplateId("");
    localStorage.removeItem(DRAFT_STORAGE_KEY);
    setExercise({
      session: "",
      title: "",
      description: "",
      videoPath: DEFAULT_VIDEO_PATH
    });
  }

  async function fetchTemplates() {
    try {
      const response = await axios.get(`${API_URL}/training/user-sessions`, {
        headers: {
          "Content-Type": "application/json",
          token: localStorage.getItem("token")
        }
      });
      setAvailableTemplates(response.data || []);
    } catch (error) {
      setErrorMessage("Kunde inte hämta befintliga pass");
    }
  }

  const applyTemplate = useCallback(async (templateId, mode = "template") => {
    if (!templateId) {
      return;
    }

    try {
      setIsLoadingTemplate(true);
      const response = await axios.get(`${API_URL}/training/session-by-id`, {
        headers: {
          token: localStorage.getItem("token")
        },
        params: {
          sessionId: templateId
        }
      });

      const sourceSession = response.data;
      setSelectedTemplateId(templateId);
      setSession({
        title: mode === "duplicate" ? `${sourceSession.title} (kopia)` : `${sourceSession.title} - nytt pass`,
        description: sourceSession.description || ""
      });
      setStations((sourceSession.station || []).map((station) => createDraftStation(station)));
      setStationDraft(createEmptyStationDraft());
      setErrorMessage("");
      setSuccessMessage(
        mode === "duplicate"
          ? "Passet laddades som en kopia som du kan justera innan du sparar."
          : "Mallen är laddad. Justera innehållet och spara när du är klar."
      );
      setExercise((previous) => ({
        ...previous,
        videoPath: DEFAULT_VIDEO_PATH
      }));
    } catch (error) {
      setErrorMessage("Kunde inte läsa in mallen");
      setSuccessMessage("");
    } finally {
      setIsLoadingTemplate(false);
    }
  }, [setExercise]);

  function addStationToDraft() {
    if (!stationDraft.title.trim()) {
      setErrorMessage("Ange en titel för stationen");
      return;
    }

    if (stationDraft.videoPath === DEFAULT_VIDEO_PATH) {
      setErrorMessage("Välj en video för stationen");
      return;
    }

    if (stations.some((station) => station.title.trim().toLowerCase() === stationDraft.title.trim().toLowerCase())) {
      setErrorMessage("Det finns redan en station med den titeln i passet");
      return;
    }

    setStations((previousStations) => [
      ...previousStations,
      createDraftStation(stationDraft)
    ]);
    setStationDraft(createEmptyStationDraft());
    setExercise((previous) => ({
      ...previous,
      videoPath: DEFAULT_VIDEO_PATH
    }));
    setErrorMessage("");
    setSuccessMessage("Stationen lades till i utkastet.");
    report("station-added");
  }

  function removeStationFromDraft(draftId) {
    setStations((previousStations) => previousStations.filter((station) => station.draftId !== draftId));
  }

  function duplicateStationInDraft(draftId) {
    const station = stations.find((item) => item.draftId === draftId);
    if (!station) {
      return;
    }

    const existingTitles = new Set(stations.map((item) => item.title.toLowerCase()));
    let suffix = 2;
    let nextTitle = `${station.title} kopia`;
    while (existingTitles.has(nextTitle.toLowerCase())) {
      nextTitle = `${station.title} kopia ${suffix}`;
      suffix += 1;
    }

    setStations((previousStations) => {
      const sourceIndex = previousStations.findIndex((item) => item.draftId === draftId);
      const duplicatedStation = createDraftStation({
        ...station,
        title: nextTitle,
        videoPath: station.videoPath
      });
      const updatedStations = [...previousStations];
      updatedStations.splice(sourceIndex + 1, 0, duplicatedStation);
      return updatedStations;
    });
    setSuccessMessage("Stationen duplicerades i utkastet.");
    setErrorMessage("");
  }

  function handleDragStart(draftId) {
    draggedStationId.current = draftId;
  }

  function handleDrop(targetDraftId) {
    if (!draggedStationId.current || draggedStationId.current === targetDraftId) {
      draggedStationId.current = null;
      return;
    }

    setStations((previousStations) => {
      const sourceIndex = previousStations.findIndex((station) => station.draftId === draggedStationId.current);
      const targetIndex = previousStations.findIndex((station) => station.draftId === targetDraftId);
      if (sourceIndex < 0 || targetIndex < 0) {
        return previousStations;
      }

      return moveItem(previousStations, sourceIndex, targetIndex);
    });

    draggedStationId.current = null;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (!session.title.trim()) {
      setErrorMessage("Ange en titel för passet");
      return;
    }

    if (stations.length === 0) {
      setErrorMessage("Lägg till minst en station innan du sparar passet");
      return;
    }

    try {
      setIsSaving(true);
      setErrorMessage("");
      setSuccessMessage("");

      const sessionResponse = await axios.post(`${API_URL}/training/add-session`,
        {
          title: session.title.trim(),
          description: session.description.trim()
        },
        {
          headers: {
            "Content-Type": "application/json",
            token: localStorage.getItem("token")
          }
        });

      const createdSession = sessionResponse.data;
      for (const draftStation of stations) {
        await axios.post(`${API_URL}/training/add-station`,
          {
            sessionId: createdSession._id,
            title: draftStation.title.trim(),
            description: draftStation.description.trim(),
            videoPath: draftStation.videoPath
          },
          {
            headers: {
              "Content-Type": "application/json",
              token: localStorage.getItem("token")
            }
          });
      }

      report("session-saved");
      resetBuilder();
      navigate("/managesession", {
        state: {
          session: createdSession._id,
          success: "Passet skapades och stationerna lades till."
        }
      });
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Kunde inte skapa passet");
    } finally {
      setIsSaving(false);
    }
  }

  useEffect(() => {
    fetchTemplates();

    const storedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (storedDraft) {
      try {
        const parsedDraft = JSON.parse(storedDraft);
        if (parsedDraft.session) {
          setSession(parsedDraft.session);
        }
        if (parsedDraft.stationDraft) {
          setStationDraft(parsedDraft.stationDraft);
        }
        if (Array.isArray(parsedDraft.stations)) {
          setStations(parsedDraft.stations);
        }
        if (parsedDraft.selectedTemplateId) {
          setSelectedTemplateId(parsedDraft.selectedTemplateId);
        }
      } catch (error) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
      }
    }

    hasHydratedDraft.current = true;
  }, []);

  useEffect(() => {
    if (!hasHydratedDraft.current) {
      return;
    }
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draftPayload));
  }, [draftPayload]);

  useEffect(() => {
    if (exercise.videoPath && exercise.videoPath !== DEFAULT_VIDEO_PATH && exercise.videoPath !== stationDraft.videoPath) {
      setStationDraft((previous) => ({
        ...previous,
        videoPath: exercise.videoPath
      }));
      setExercise((previous) => ({
        ...previous,
        videoPath: DEFAULT_VIDEO_PATH
      }));
    }
  }, [exercise.videoPath, setExercise, stationDraft.videoPath]);

  useEffect(() => {
    if (initializedFromState.current) {
      return;
    }

    const incomingSessionId = location.state?.templateSessionId || location.state?.duplicateSessionId;
    if (incomingSessionId) {
      const mode = location.state?.duplicateSessionId ? "duplicate" : "template";
      applyTemplate(incomingSessionId, mode);
    }
    initializedFromState.current = true;
  }, [applyTemplate, location.state]);

  return (
    <div className="flex flex-col items-center px-3">
      <div className="my-4 w-full max-w-4xl rounded-[28px] bg-gradient-to-br from-slate-50 via-white to-sky-50 p-5 shadow-sm ring-1 ring-slate-200/80">
        <form onSubmit={handleSubmit}>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(320px,0.8fr)]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Pass</p>
              <h1 className="mt-2 text-lg font-bold text-slate-800">Skapa pass i ett steg</h1>
              <GuideButton id="create" />
              <p className="mt-2 text-sm text-slate-600">
                Bygg hela passet direkt här, återanvänd ett befintligt pass som mall och ordna stationerna i den ordning du vill ha dem.
              </p>

              <div className="mt-5 rounded-[24px] border border-sky-200 bg-white/80 p-4">
                <label className="text-sm font-semibold text-slate-700" htmlFor="template-select">
                  Starta från mall
                </label>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <select
                    id="template-select"
                    value={selectedTemplateId}
                    onChange={(event) => setSelectedTemplateId(event.target.value)}
                    className="flex-1 rounded-2xl border border-sky-200 bg-white px-4 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                  >
                    <option value="">Tomt pass</option>
                    {availableTemplates.map((template) => (
                      <option key={template._id} value={template._id}>
                        {template.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
                    onClick={() => applyTemplate(selectedTemplateId)}
                    disabled={!selectedTemplateId || isLoadingTemplate}
                  >
                    {isLoadingTemplate ? "Laddar..." : "Ladda mall"}
                  </button>
                </div>
              </div>

              <div className="mt-5 rounded-[24px] border border-slate-200 bg-white/85 p-4">
                <div className="grid gap-4">
                  <div>
                    <label className="text-sm font-semibold text-slate-700" htmlFor="session-title">
                      Titel
                    </label>
                    <input
                      data-guide="session-title"
                      id="session-title"
                      value={session.title}
                      required
                      className="mt-2 w-full rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                      placeholder="Titel på passet"
                      onChange={(event) => setSession({ ...session, title: event.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-slate-700" htmlFor="session-description">
                      Beskrivning
                    </label>
                    <textarea
                      id="session-description"
                      value={session.description}
                      className="mt-2 min-h-[110px] w-full rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                      placeholder="Kort beskrivning av passet"
                      onChange={(event) => setSession({ ...session, description: event.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div className="mt-5 rounded-[24px] border border-slate-200 bg-white/85 p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-slate-700">Ny station</p>
                    <p className="mt-1 text-sm text-slate-500">Fyll i stationen och lägg till den i utkastet.</p>
                  </div>
                  <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                    {stations.length} stationer
                  </span>
                </div>
                <div className="mt-4 grid gap-3">
                  <input
                    value={stationDraft.title}
                    className="rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                    data-guide="station-title"
                    aria-label="Stationstitel"
                    placeholder="Stationstitel"
                    onChange={(event) => setStationDraft({ ...stationDraft, title: event.target.value })}
                  />
                  <textarea
                    value={stationDraft.description}
                    className="min-h-[96px] rounded-2xl border border-sky-200 bg-white px-5 py-3 shadow-sm outline-none transition-colors focus:border-sky-400"
                    placeholder="Stationsbeskrivning"
                    onChange={(event) => setStationDraft({ ...stationDraft, description: event.target.value })}
                  />
                  <div className="flex flex-col gap-3 sm:flex-row">
                    <input
                      className="flex-1 rounded-2xl border border-sky-200 bg-white px-5 py-3 text-slate-500 shadow-sm"
                      value={stationDraft.videoPath}
                      disabled
                    />
                    <button
                      type="button"
                      className="rounded-[20px] border border-sky-200/80 bg-white px-5 py-3 text-sm font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-50"
                      data-guide="choose-video"
                      onClick={() => navigate("/videos", { state: { source: "create-session" } })}
                    >
                      Välj video
                    </button>
                    <button
                      type="button"
                      className="rounded-[20px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md"
                      data-guide="add-station"
                      onClick={addStationToDraft}
                    >
                      Lägg till station
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-[24px] border border-slate-200 bg-white/85 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p data-guide="station-order" className="text-sm font-semibold text-slate-700">Stationsordning</p>
                  <p className="mt-1 text-sm text-slate-500">Dra och släpp för att ändra ordning innan du sparar.</p>
                </div>
                <button
                  type="button"
                  className="rounded-full border border-slate-200 bg-slate-100 px-4 py-2 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-200"
                  onClick={resetBuilder}
                >
                  Rensa utkast
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {stations.length === 0 && (
                  <div className="rounded-[22px] border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                    Lägg till stationer eller ladda en mall för att börja bygga passet.
                  </div>
                )}

                {stations.map((station, index) => (
                  <div
                    key={station.draftId}
                    draggable
                    onDragStart={() => handleDragStart(station.draftId)}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => handleDrop(station.draftId)}
                    className="rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-white via-sky-50 to-cyan-50 px-4 py-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-semibold text-sky-700 shadow-sm ring-1 ring-sky-100">
                            {index + 1}
                          </span>
                          <p className="truncate text-sm font-semibold text-slate-800">{station.title}</p>
                        </div>
                        {station.description && (
                          <p className="mt-2 text-sm text-slate-600">{station.description}</p>
                        )}
                        <p className="mt-2 truncate text-xs text-slate-500">{station.videoPath}</p>
                      </div>
                      <span className="rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-sky-700">
                        Dra
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        className="rounded-full border border-sky-200 bg-white px-4 py-2 text-xs font-semibold text-sky-700 shadow-sm transition-colors hover:bg-sky-100"
                        onClick={() => duplicateStationInDraft(station.draftId)}
                      >
                        Duplicera station
                      </button>
                      <button
                        type="button"
                        className="rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-700 shadow-sm transition-colors hover:bg-red-50"
                        onClick={() => removeStationFromDraft(station.draftId)}
                      >
                        Ta bort
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-[22px] border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-600">
                Väljer du en befintlig mall kopieras stationerna in i utkastet. Passet skapas först när du trycker på spara.
              </div>
            </div>
          </div>

          {(errorMessage || successMessage) && (
            <div className="mt-5 space-y-3">
              {errorMessage && (
                <div className="rounded-2xl border border-red-300 bg-red-50 px-5 py-3 text-sm text-red-800">
                  {errorMessage}
                </div>
              )}
              {successMessage && (
                <div className="rounded-2xl border border-green-300 bg-green-50 px-5 py-3 text-sm text-green-800">
                  {successMessage}
                </div>
              )}
            </div>
          )}

          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
            <button
              type="button"
              className="rounded-[22px] border border-slate-200 bg-white px-5 py-3 font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-100"
              onClick={() => navigate("/my-workouts")}
            >
              Till mina pass
            </button>
            <button
              type="submit"
              className="rounded-[22px] border border-sky-200/80 bg-gradient-to-r from-sky-700 to-cyan-500 px-5 py-3 font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-60"
              data-guide="save-session"
              disabled={isSaving}
            >
              {isSaving ? "Sparar pass..." : "Spara pass"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateSession;
